import type { Timestamp } from 'firebase/firestore';

/**
 * Daily Bite — Phase 3 correlation unlocks.
 *
 * Lives at users/{uid}/correlations/current. Owner-only reads: this is an
 * inference layer over the daily_entries log, and the member has not
 * consented to share inferences with clinicians or partners. See the
 * firestore.rules block for the hard boundary.
 *
 * Editorial rule every card has to pass: observe, don't claim. Cards are
 * written with "looks like" / "appears to" / "worth watching" / "too early
 * to say". Never causal. If a card would require a causal reading to be
 * useful, we don't ship the card.
 *
 * Threshold rule every card has to pass: ≥ 3 distinct ISO weeks of logged
 * data before we show a real observation. Before that, status is
 * 'gathering' and the UI renders a "still gathering — N of 3 weeks"
 * placeholder so the member knows something is coming without us
 * pretending to have signal we don't have.
 */

export type CorrelationId =
  | 'caffeine_sleep'
  | 'caffeine_mood_nextday'
  | 'alcohol_hrv'
  | 'protein_satiety'
  | 'mood_digestion';

export type CorrelationCardStatus =
  /** Below the 3-week longitudinal threshold. */
  | 'gathering'
  /** Above threshold, but not enough datapoints on either side of the split. */
  | 'insufficient_signal'
  /** Above threshold, signal present, card has real copy to show. */
  | 'observed'
  /** Member has turned this correlation off in settings. */
  | 'opted_out';

/**
 * Per-correlation opt-in toggles. Defaults follow the clinician-sharing
 * pattern: the low-stigma ones default ON, the sensitive ones default OFF.
 * Alcohol and mood×digestion cross enough personal territory that we want
 * the member to opt in deliberately.
 */
export interface CorrelationOptIns {
  caffeine_sleep: boolean;
  caffeine_mood_nextday: boolean;
  alcohol_hrv: boolean;
  protein_satiety: boolean;
  mood_digestion: boolean;
}

export const DEFAULT_CORRELATION_OPTINS: CorrelationOptIns = {
  caffeine_sleep: true,
  caffeine_mood_nextday: true,
  alcohol_hrv: false,
  protein_satiety: true,
  mood_digestion: false,
};

export const CORRELATION_LABELS: Record<CorrelationId, string> = {
  caffeine_sleep: 'Caffeine timing · sleep',
  caffeine_mood_nextday: 'Caffeine timing · next-day mood',
  alcohol_hrv: 'Alcohol · recovery',
  protein_satiety: 'Protein · satiety',
  mood_digestion: 'Mood · digestion',
};

export const CORRELATION_DESCRIPTIONS: Record<CorrelationId, string> = {
  caffeine_sleep:
    'How the time you had your last caffeine lines up with how you slept that night.',
  caffeine_mood_nextday:
    'Whether late caffeine days tend to show up in how the next day feels.',
  alcohol_hrv:
    'How drinking days line up with your wearable recovery the following morning.',
  protein_satiety:
    'Whether hitting a protein target tracks with steadier mood and a fuller reflection the next day.',
  mood_digestion:
    'Observing, not claiming — the gut-brain axis runs both ways. We just note when the two patterns move together.',
};

/* ────────────────────────────────────────────────────────────────────────────
   Card record. One per CorrelationId in the projection doc. The
   status-keyed copy in services/correlationService.ts drives what actually
   renders; this record just captures the facts the card is built from.
   ──────────────────────────────────────────────────────────────────────── */

export interface CorrelationCardRecord {
  id: CorrelationId;
  status: CorrelationCardStatus;
  /** Weeks of longitudinal data the member has at compute time (capped at 3). */
  weeksCovered: number;
  /** Distinct days of logged data inside the compute window. */
  daysCovered: number;
  /** Count of datapoints that fed the "high" side of the split, if relevant. */
  highBucketDays?: number;
  /** Count of datapoints that fed the "low" / reference side, if relevant. */
  lowBucketDays?: number;
  /**
   * Direction of the observed pattern, strictly for rendering the right
   * copy variant. 'none' means above threshold but no meaningful split.
   */
  direction?: 'associated' | 'inverse' | 'flat' | 'none';
  /**
   * Effect size, unit-less. Different meanings per card (delta in mood
   * valence, delta in HRV, etc.) — kept as a number so we can tune the
   * rendering threshold without changing the storage shape.
   */
  effect?: number;
  /**
   * Headline line rendered on the intelligence card. Written by the
   * service, not the UI, so the copy stays centralised with the editorial
   * rules in one place.
   */
  headline: string;
  /** Second line — the "worth watching" hedge + what the member could try. */
  body: string;
  /** Optional sub-note for unusual states (e.g. "needs a wearable sync"). */
  footnote?: string;
}

export interface CorrelationsDocRecord {
  cards: CorrelationCardRecord[];
  /** Lookback window in days the service used for this compute. */
  windowDays: number;
  /** Distinct ISO weeks observed inside the window (capped at 3). */
  weeksCovered: number;
  /** Distinct logged days (completeness > 0) inside the window. */
  daysCovered: number;
  /** Set if the member has at least one wearable observation to read. */
  wearableAvailable: boolean;
  lastComputedAt?: Timestamp | null;
  /** YYYY-MM-DD of the day we computed on, so we can detect stale doc states. */
  lastComputedForDate: string;
}

export const THREE_WEEK_THRESHOLD = 3;

export const EMPTY_CORRELATIONS_DOC: CorrelationsDocRecord = {
  cards: [],
  windowDays: 28,
  weeksCovered: 0,
  daysCovered: 0,
  wearableAvailable: false,
  lastComputedAt: null,
  lastComputedForDate: '',
};

export const ALL_CORRELATION_IDS: CorrelationId[] = [
  'caffeine_sleep',
  'caffeine_mood_nextday',
  'alcohol_hrv',
  'protein_satiety',
  'mood_digestion',
];
